"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { Transition } from "framer-motion";
import { useOnClickOutside } from "usehooks-ts";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

interface Tab {
  title: string;
  icon?: LucideIcon;
  imageSrc?: string;
  imageWrapperClassName?: string;
  selectedImageWrapperClassName?: string;
  imageClassName?: string;
  selectedImageClassName?: string;
  type?: never;
}

interface Separator {
  type: "separator";
  title?: never;
  icon?: never;
  imageSrc?: never;
  imageWrapperClassName?: never;
  selectedImageWrapperClassName?: never;
  imageClassName?: never;
  selectedImageClassName?: never;
}

type TabItem = Tab | Separator;

interface ExpandableTabsProps {
  tabs: TabItem[];
  className?: string;
  activeColor?: string;
  onChange?: (index: number | null) => void;
  selectedIndex?: number | null;
  tabsContainerClassName?: string;
  actions?: React.ReactNode;
  actionsClassName?: string;
}

const buttonVariants = {
  initial: {
    gap: 0,
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
  },
  animate: (isSelected: boolean) => ({
    gap: isSelected ? "0.2rem" : 0,
    paddingLeft: isSelected ? "1.2rem" : "0.5rem",
    paddingRight: isSelected ? "1.2rem" : "0.5rem",
  }),
};

const spanVariants = {
  initial: { width: 0, opacity: 0 },
  animate: { width: "auto", opacity: 1 },
  exit: { width: 0, opacity: 0 },
};

const transition: Transition = {
  delay: 0.1,
  type: "spring",
  bounce: 0,
  duration: 0.6,
};

export function ExpandableTabs({
  tabs,
  className,
  activeColor = "text-foreground",
  onChange,
  selectedIndex,
  tabsContainerClassName,
  actions,
  actionsClassName,
}: ExpandableTabsProps) {
  const isControlled = selectedIndex !== undefined;
  const [uncontrolledSelected, setUncontrolledSelected] =
    React.useState<number | null>(null);
  const selected = isControlled ? selectedIndex ?? null : uncontrolledSelected;
  const outsideClickRef = React.useRef(null);

  useOnClickOutside(outsideClickRef, () => {
    if (!isControlled) {
      setUncontrolledSelected(null);
    }
    onChange?.(null);
  });

  const handleSelect = (index: number) => {
    if (!isControlled) {
      setUncontrolledSelected(index);
    }
    onChange?.(index);
  };

  const Separator = () => (
    <div className="mx-1 h-[24px] w-[1.2px] bg-border" aria-hidden="true" />
  );

  return (
    <div
      ref={outsideClickRef}
      className={cn(
        "flex w-full items-center gap-2 md:gap-3 px-2",
        className
      )}
    >
      <div
        className={cn(
          "flex w-full items-center gap-1 md:gap-3 rounded-2xl md:rounded-3xl border bg-background p-1 px-2 pt-2.5 md:p-1.5 md:px-5 shadow-sm md:shadow-md my-1 overflow-x-auto scrollbar-hide",
          tabsContainerClassName
        )}
      >
        {tabs.map((tab, index) => {
          if (tab.type === "separator") {
            return <Separator key={`separator-${index}`} />;
          }

          const Icon = tab.icon;
          return (
            <motion.button
              key={tab.title}
              variants={buttonVariants}
              initial={false}
              animate="animate"
              custom={selected === index}
              onClick={() => handleSelect(index)}
              transition={transition}
              className={cn(
                "relative flex shrink-0 items-center rounded-xl md:rounded-2xl px-2.5 md:px-3.5 py-0 md:py-0.5 text-[11px] md:text-[16px] transition-colors duration-300 overflow-visible",
                selected === index
                  ? cn("bg-muted", activeColor)
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              {tab.imageSrc ? (
                <span
                  className={cn(
                    "relative flex items-center justify-center rounded-xl transition-all duration-200",
                    selected === index
                      ? "overflow-visible h-10 w-10 scale-[1.05]"
                      : "overflow-hidden h-7 w-7",
                    tab.imageWrapperClassName,
                    selected === index
                      ? tab.selectedImageWrapperClassName
                      : undefined
                  )}
                >
                  <img
                    src={tab.imageSrc}
                    alt={tab.title}
                    width={64}
                    height={64}
                    className={cn(
                      "pointer-events-none rounded-3xl object-contain transition-transform duration-200",
                      selected === index
                        ? "h-[44px] w-[44px]"
                        : "h-[28px] w-[28px]",
                      tab.imageClassName,
                      selected === index
                        ? tab.selectedImageClassName
                        : undefined
                    )}
                  />
                </span>
              ) : Icon ? (
                <Icon size={20} />
              ) : null}
              <AnimatePresence initial={false}>
                {selected === index && (
                  <motion.span
                    variants={spanVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={transition}
                    className="overflow-hidden whitespace-nowrap"
                  >
                    {tab.title}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          );
        })}
        {actions ? (
          <div
            className={cn(
              "flex shrink-0 items-center gap-1 ml-auto",
              actionsClassName
            )}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
